import { getMessages } from "@/i18n/runtime";
import type {
  LinkAccountResult,
  LinkAccountService,
  OAuthProvider,
} from "@/auth/linkAccountService";
import type { AuthState, AuthStateSnapshot } from "@/auth/authState";

export interface SettingsAccountSection {
  readonly root: HTMLDivElement;
  update(snapshot: AuthStateSnapshot): void;
  dispose(): void;
}

export interface CreateSettingsAccountSectionOptions {
  readonly authState: AuthState;
  readonly linkService: LinkAccountService;
  readonly oauthProviders?: readonly OAuthProvider[];
}

type Status = "idle" | "pending" | "success" | "error";

export function createSettingsAccountSection(
  options: CreateSettingsAccountSectionOptions,
): SettingsAccountSection {
  const messages = getMessages().settings.data.account;
  const root = document.createElement("div");
  root.className = "hover-idle-account-panel";

  const title = document.createElement("h3");
  title.className = "hover-idle-account-title";
  title.textContent = messages.title;

  const statusLine = document.createElement("p");
  statusLine.className = "hover-idle-account-status";

  const hintLine = document.createElement("p");
  hintLine.className = "hover-idle-account-hint";

  const form = document.createElement("form");
  form.className = "hover-idle-account-form";
  form.noValidate = true;

  const emailLabel = document.createElement("label");
  emailLabel.className = "hover-idle-account-field";
  const emailLabelText = document.createElement("span");
  emailLabelText.textContent = messages.emailLabel;
  const emailInput = document.createElement("input");
  emailInput.type = "email";
  emailInput.required = true;
  emailInput.placeholder = messages.emailPlaceholder;
  emailInput.autocomplete = "email";
  emailInput.className = "hover-idle-account-input";
  emailLabel.append(emailLabelText, emailInput);

  const submit = document.createElement("button");
  submit.type = "submit";
  submit.className = "hover-idle-account-button";
  submit.textContent = messages.emailSubmit;

  const feedback = document.createElement("p");
  feedback.className = "hover-idle-account-feedback";
  feedback.setAttribute("role", "status");
  feedback.setAttribute("aria-live", "polite");

  form.append(emailLabel, submit, feedback);

  const oauthList = document.createElement("div");
  oauthList.className = "hover-idle-account-oauth";
  for (const provider of options.oauthProviders ?? []) {
    const button = document.createElement("button");
    button.type = "button";
    button.className =
      "hover-idle-account-button hover-idle-account-oauth-button";
    button.textContent = messages.oauthSubmit(provider);
    button.dataset.provider = provider;
    button.addEventListener("click", () => {
      void handleOAuthClick(provider);
    });
    oauthList.append(button);
  }

  root.append(title, statusLine, hintLine, form, oauthList);

  let status: Status = "idle";

  const setStatus = (next: Status, message: string): void => {
    status = next;
    feedback.textContent = message;
    feedback.dataset.status = next;
  };

  const setEmailControlsDisabled = (disabled: boolean): void => {
    emailInput.disabled = disabled;
    submit.disabled = disabled;
  };

  const handleResult = (result: LinkAccountResult): void => {
    if (result.ok) {
      setStatus("success", messages.emailSent);
      return;
    }
    const reasonMessage =
      result.reason === "invalid-email"
        ? messages.errors.invalidEmail
        : result.reason === "no-anonymous-session"
          ? messages.errors.noAnonymousSession
          : result.reason === "not-implemented"
            ? messages.oauthNotImplemented
            : messages.errors.linkFailed;
    setStatus("error", reasonMessage);
  };

  const handleSubmit = async (event: SubmitEvent): Promise<void> => {
    event.preventDefault();
    if (status === "pending") return;
    setStatus("pending", "");
    setEmailControlsDisabled(true);
    try {
      const result = await options.linkService.linkAccountWithEmail(
        emailInput.value,
      );
      handleResult(result);
    } catch (error) {
      console.warn("linkAccountWithEmail threw:", error);
      setStatus("error", messages.errors.generic);
    } finally {
      setEmailControlsDisabled(false);
    }
  };

  const handleOAuthClick = async (provider: OAuthProvider): Promise<void> => {
    if (status === "pending") return;
    setStatus("pending", "");
    try {
      const result = await options.linkService.linkAccountWithOAuth(provider);
      handleResult(result);
    } catch (error) {
      console.warn("linkAccountWithOAuth threw:", error);
      setStatus("error", messages.errors.generic);
    }
  };

  form.addEventListener("submit", (event) => {
    void handleSubmit(event);
  });

  const renderOwner = (snapshot: AuthStateSnapshot): void => {
    const ownerKind = snapshot.owner.ownerKind;
    root.dataset.ownerKind = ownerKind;
    if (ownerKind === "signedIn") {
      statusLine.textContent = messages.statusSignedIn(snapshot.owner.ownerId);
      hintLine.textContent = messages.statusSignedInHint;
      form.hidden = true;
      oauthList.hidden = true;
    } else {
      statusLine.textContent = messages.statusGuest;
      hintLine.textContent = messages.statusGuestHint;
      form.hidden = false;
      oauthList.hidden = (options.oauthProviders ?? []).length === 0;
    }
  };

  renderOwner(options.authState.getSnapshot());
  const unsubscribe = options.authState.subscribe(renderOwner);

  return {
    root,
    update(snapshot) {
      renderOwner(snapshot);
    },
    dispose() {
      unsubscribe();
      root.remove();
    },
  };
}
